import {
  AppStateConfigModel,
  AppStateModel,
  CashcutListPaginationModel,
  TransactionListPaginationModel,
} from './appState.model';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {
  SetAccentColor,
  SetConfig,
  SetGatewayConfig,
  SetLanguage,
  SetOverviewMode,
  SetScrolledBody,
  SetTheme,
  StartLoading,
  StopLoading,
  SwitchTheme,
} from './actions/general.actions';
import {of} from 'rxjs';
import {catchError, mergeMap, tap} from 'rxjs/operators';
import {LanguageService} from '../shared/services/language.service';
import {Injectable} from '@angular/core';
import {LanguageModel} from '../shared/models/language.model';
import {ThemingService} from '../shared/services/theming.service';
import {Themes} from '../shared/enum/themes.enum';
import {
  GetCashcutDetails,
  GetCashcutList,
  GetMerchantDetails,
  GetMoreCashcutListItems,
  GetMoreTransactionListItems,
  GetTransactionDetails,
  GetTransactionList,
  Login,
  Logout,
  SetRequestFilter, UpdateEmail,
  UpdatePin,
  UserRestore,
} from './actions/merchant.actions';
import {MerchantResponseModel} from '../shared/services/merchant/merchantResponse.model';
import {MerchantService} from '../shared/services/merchant/merchant.service';
import {MerchantModel} from '../shared/models/merchant.model';
import {TransactionListItemModel} from '../shared/models/transactionListItem.model';
import {TransactionDetailModel} from '../shared/models/transactionDetail.model';
import {Navigate} from '@ngxs/router-plugin';
import {
  RouteAccount,
  RouteCashcutDetails, RouteEmailMigration,
  RouteHome,
  RouteImprint,
  RouteLogin,
  RouteRegister,
  RouteTransactionDetails,
} from './actions/routing.actions';
import {RequestFilterModel} from '../shared/models/requestFilterModel';
import {TransactionListItemResponseModel} from '../shared/services/merchant/transactionListItemResponse.model';
import {TransactionDetailResponseModel} from '../shared/services/merchant/transactionDetailResponse.model';
import {MerchantPosResponseModel} from '../shared/services/merchant/merchantPosResponse.model';
import {PosModel} from '../shared/models/pos.model';
import {GatewayConfigModel} from '../shared/services/gateway-config/gatewayConfig.model';
import {CashcutListItemResponseModel} from '../shared/services/merchant/cashcutListItemResponse.model';
import {CashcutListItemModel} from '../shared/models/cashcutListItem.model';
import {CashcutDetailModel} from '../shared/models/cashcutDetail.model';
import {CashcutDetailResponseModel} from '../shared/services/merchant/cashcutDetailResponse.model';
import {OverviewModesEnum} from '../shared/enum/overviewModes.enum';
import {ReportFilterModel} from '../shared/models/reportFilter.model';
import {ReportModel} from '../shared/models/report.model';
import {
  DownloadCustomerSalesReport,
  GetCustomerSalesReport,
  SetCustomerSalesReportFilter,
} from './actions/report.actions';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {
  getDateString,
  getDateTimeString,
  getEndOfToday,
  getStartOfCurrentMonth,
} from '../shared/helper/utils';

const DEFAULT_FILTER: RequestFilterModel = {
  pos: '',
  fromDate: getStartOfCurrentMonth(),
  toDate: getEndOfToday(),
};

const DEFAULT_CUSTOMERSALES_REPORTS_FILTER: ReportFilterModel = {
  cardId: '',
  deviceId: '',
  posId: '',
  fromDate: getStartOfCurrentMonth(),
  toDate: getEndOfToday(),
};

@State<AppStateModel>({
  defaults: {
    loading: true,
    isScrolled: false,
    gatewayConfig: null,
    config: {
      accentColor: '#ddd',
      accentFontColor: '#111',
      availableLanguages: [],
      gateway: null,
      maintenance: false,
      lazyLoadingChunkSize: 20,
      showPartners: true,
      showFooter: false,
      showImprint: false,
      showPrivacy: false,
      showTos: false,
    },
    currentLanguage: null,
    currentTheme: Themes.light,
    overviewMode: OverviewModesEnum.TRANSACTIONS,
    authString: null,
    currentMerchant: null,
    transactionDetails: null,
    transactionList: null,
    requestFilter: DEFAULT_FILTER,
    cashcutDetails: null,
    cashcutList: null,
    customerSalesReport: {
      count: 0,
      data: '',
    },
    customerSalesReportFilter: DEFAULT_CUSTOMERSALES_REPORTS_FILTER,
  },
  name: 'AppState',
})
@Injectable()
export class AppState {
  private readonly CREDENTIALS_KEY = 'PROFISHOP_MERCHANT_CREDENTIALS';

  // region selectors

  @Selector()
  static config$(state: AppStateModel): AppStateConfigModel {
    return state.config;
  }

  @Selector()
  static gatewayConfig$(state: AppStateModel): GatewayConfigModel {
    return state.gatewayConfig;
  }

  @Selector()
  static isScrolled$(state: AppStateModel): boolean {
    return state.isScrolled;
  }

  @Selector()
  static loading$(state: AppStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static currentLanguage$(state: AppStateModel): LanguageModel {
    return state.currentLanguage;
  }

  @Selector()
  static overviewMode$(state: AppStateModel): OverviewModesEnum {
    return state.overviewMode;
  }

  @Selector()
  static currentMerchant$(state: AppStateModel): MerchantModel {
    return state.currentMerchant;
  }

  @Selector()
  static authString$(state: AppStateModel): string {
    return state.authString;
  }

  @Selector()
  static transactionList$(
    state: AppStateModel
  ): TransactionListPaginationModel {
    return state.transactionList;
  }

  @Selector()
  static requestFilter$(state: AppStateModel): RequestFilterModel {
    return state.requestFilter;
  }

  @Selector()
  static transactionDetails$(state: AppStateModel): TransactionDetailModel {
    return state.transactionDetails;
  }

  @Selector()
  static cashcutList$(state: AppStateModel): CashcutListPaginationModel {
    return state.cashcutList;
  }

  @Selector()
  static cashcutDetails$(state: AppStateModel): CashcutDetailModel {
    return state.cashcutDetails;
  }

  @Selector()
  static customerSalesReport$(state: AppStateModel): ReportModel {
    return state.customerSalesReport;
  }

  @Selector()
  static customerSalesReportFilter$(state: AppStateModel): ReportFilterModel {
    return state.customerSalesReportFilter;
  }

  // endregion

  constructor(
    private languageService: LanguageService,
    private themingService: ThemingService,
    private merchantService: MerchantService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
  }

  // region general action

  @Action(StartLoading)
  private startLoading(ctx: StateContext<AppStateModel>): any {
    ctx.setState({...ctx.getState(), loading: true});
  }

  @Action(StopLoading)
  private stopLoading(ctx: StateContext<AppStateModel>): any {
    ctx.setState({...ctx.getState(), loading: false});
  }

  @Action(SetLanguage)
  private setLanguage(
    ctx: StateContext<AppStateModel>,
    action: SetLanguage
  ): any {
    return this.languageService.setLanguage(action.language.key).pipe(
      tap(() => {
        ctx.setState({
          ...ctx.getState(),
          currentLanguage: action.language,
        });
      })
    );
  }

  @Action(SetScrolledBody)
  private setScrolledBody(
    ctx: StateContext<AppStateModel>,
    action: SetScrolledBody
  ): any {
    ctx.setState({
      ...ctx.getState(),
      isScrolled: action.isScrolled,
    });
  }

  @Action(SetGatewayConfig)
  private setGatewayConfig(
    ctx: StateContext<AppStateModel>,
    action: SetGatewayConfig
  ): any {
    return ctx.setState({
      ...ctx.getState(),
      gatewayConfig: action.config,
    });
  }

  @Action(SetConfig)
  private setConfig(ctx: StateContext<AppStateModel>, action: SetConfig): any {
    const config = action.config;

    const setLanguageObservable =
      !ctx.getState().currentLanguage && config.availableLanguages.length > 0
        ? ctx.dispatch(new SetLanguage(config.availableLanguages[0]))
        : of();

    return setLanguageObservable.pipe(
      tap(() =>
        ctx.dispatch(
          new SetAccentColor(config.accentColor, config.accentFontColor)
        )
      ),
      tap(() => {
        ctx.setState({
          ...ctx.getState(),
          config,
        });
      })
    );
  }

  @Action(SetAccentColor)
  private setAccentColor(
    ctx: StateContext<AppStateModel>,
    action: SetAccentColor
  ): any {
    this.themingService.setAccentColor(action.bgColor, action.fontColor);
  }

  @Action(SwitchTheme)
  private switchTheme(
    ctx: StateContext<AppStateModel>,
    action: SwitchTheme
  ): any {
    const newTheme =
      ctx.getState().currentTheme === Themes.light ? Themes.dark : Themes.light;
    ctx.dispatch(new SetTheme(newTheme));
  }

  @Action(SetTheme)
  private setTheme(ctx: StateContext<AppStateModel>, action: SetTheme): any {
    this.themingService.setTheme(action.theme);
    ctx.setState({
      ...ctx.getState(),
      currentTheme: action.theme,
    });
  }

  @Action(SetOverviewMode)
  private setOverviewMode(
    ctx: StateContext<AppStateModel>,
    action: SetOverviewMode
  ): any {
    ctx.setState({
      ...ctx.getState(),
      overviewMode: action.mode,
    });
  }

  // endregion

  // region user

  @Action(UserRestore)
  private userRestore(ctx: StateContext<AppStateModel>): any {
    const credentials = this.getCredentials();
    if (credentials) {
      return ctx.dispatch(new Login(credentials.cardId, credentials.pin)).pipe(
        catchError(() => {
          return ctx.dispatch(new Logout());
        })
      );
    } else {
      return ctx.dispatch(new StopLoading());
    }
  }

  @Action(Login)
  private login(ctx: StateContext<AppStateModel>, action: Login): any {
    return ctx.dispatch(new StartLoading()).pipe(
      tap(() => {
        ctx.setState({
          ...ctx.getState(),
          authString: btoa(unescape(encodeURIComponent((`${action.card}:${action.pin}`)))),
        });
        this.saveCredentials(action.card, action.pin);
      }),
      mergeMap(() => ctx.dispatch(new GetMerchantDetails())),
      mergeMap(() => ctx.dispatch(new GetTransactionList())),
      mergeMap(() => ctx.dispatch(new GetCashcutList())),
      mergeMap(() => ctx.dispatch(new GetCustomerSalesReport())),
      mergeMap(() => ctx.dispatch(new StopLoading())),
      catchError(err => {
        ctx.setState({
          ...ctx.getState(),
          authString: null,
        });
        ctx.dispatch(new StopLoading());
        throw err;
      })
    );
  }

  @Action(Logout)
  private logout(ctx: StateContext<AppStateModel>, action: Logout): any {
    this.deleteCredentials();

    ctx.setState({
      ...ctx.getState(),
      authString: null,
      currentMerchant: null,
      overviewMode: OverviewModesEnum.TRANSACTIONS,
      transactionDetails: null,
      transactionList: null,
      requestFilter: DEFAULT_FILTER,
      cashcutDetails: null,
      cashcutList: null,
    });
    return ctx
      .dispatch(new RouteLogin())
      .pipe(mergeMap(() => ctx.dispatch(new StopLoading())));
  }

  @Action(GetMerchantDetails)
  private getCardDetails(
    ctx: StateContext<AppStateModel>,
    action: GetMerchantDetails
  ): any {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.merchantService.getPaycardDetails()),
      tap((response: MerchantResponseModel) => {
        ctx.setState({
          ...ctx.getState(),
          currentMerchant: {
            updated: response.updated,
            created: response.created,
            company: response.company,
            merchantId: response.merchantId,
            pointOfSales: response.pointOfSales.map(p =>
              this.mapExtPosToPos(p)
            ),
            status: response.status,
            email: response.email
          },
        });
      })
    );
  }

  @Action(UpdatePin)
  private updatePin(ctx: StateContext<AppStateModel>, action: UpdatePin): any {
    const state = ctx.getState();
    const merchantEmail = state.currentMerchant.email;
    const api = state.config.gateway;

    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() =>
        this.merchantService.changePin(
          merchantEmail,
          action.oldPassword,
          action.newPassword,
          api
        )
      ),
      mergeMap(() => ctx.dispatch(new Logout()))
    );
  }

  @Action(UpdateEmail)
  private updateEmail(ctx: StateContext<AppStateModel>, action: UpdateEmail): any {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() =>
        this.merchantService.changeEmail(action.newEmail)
      ),
      mergeMap(() => ctx.dispatch(new Logout()))
    );
  }

  // region transactions

  @Action(GetTransactionList)
  private getTransactions(ctx: StateContext<AppStateModel>): any {
    return this.merchantService
      .getTransactions(
        0,
        ctx.getState().config.lazyLoadingChunkSize,
        ctx.getState().requestFilter
      )
      .pipe(
        tap(transactions => {
          ctx.setState({
            ...ctx.getState(),
            transactionList: {
              page: transactions.number,
              size: transactions.size,
              isLast: transactions.last,
              items: transactions.content.map(t =>
                this.mapExtTransactionListItemToTransactionListItem(t)
              ),
              totalAmount: transactions.totalAmount,
              totalAmountPayment: transactions.totalAmountPayment,
              currency: transactions.currency,
              currencyPayment: transactions.currencyPayment,
            },
          });
        })
      );
  }

  @Action(GetMoreTransactionListItems)
  private getMoreTransactions(ctx: StateContext<AppStateModel>): any {
    const state = ctx.getState();
    const {page, size, isLast} = ctx.getState().transactionList
      ? state.transactionList
      : {page: -1, size: state.config.lazyLoadingChunkSize, isLast: false};

    if (isLast) {
      return of();
    } else {
      return this.merchantService
        .getTransactions(page + 1, size, ctx.getState().requestFilter)
        .pipe(
          tap(transactions => {
            ctx.setState({
              ...state,
              transactionList: {
                page: transactions.number,
                size: transactions.size,
                isLast: transactions.last,
                items: [
                  ...(state.transactionList && state.transactionList.items
                    ? state.transactionList.items
                    : []),
                  ...transactions.content.map(t =>
                    this.mapExtTransactionListItemToTransactionListItem(t)
                  ),
                ],
                totalAmount: transactions.totalAmount,
                totalAmountPayment: transactions.totalAmountPayment,
                currency: transactions.currency,
                currencyPayment: transactions.currencyPayment,
              },
            });
          })
        );
    }
  }

  @Action(SetRequestFilter)
  private setTransactionFilter(
    ctx: StateContext<AppStateModel>,
    action: SetRequestFilter
  ): any {
    ctx.setState({
      ...ctx.getState(),
      requestFilter: action.filter,
    });
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => ctx.dispatch(new GetTransactionList())),
      mergeMap(() => ctx.dispatch(new GetCashcutList())),
      mergeMap(() => ctx.dispatch(new StopLoading()))
    );
  }

  @Action(GetTransactionDetails)
  private getTransactionDetails(
    ctx: StateContext<AppStateModel>,
    action: GetTransactionDetails
  ): any {
    if (action.transactionId == null) {
      // clear transaction details
      ctx.setState({
        ...ctx.getState(),
        transactionDetails: null,
      });
      return of();
    }

    if (
      ctx.getState().transactionDetails &&
      ctx.getState().transactionDetails.saleId === action.transactionId
    ) {
      // do nothing, as we already have the right transaction inside the state
      return of();
    }

    return this.merchantService.getTransactionDetail(action.transactionId).pipe(
      tap(t => {
        ctx.setState({
          ...ctx.getState(),
          transactionDetails:
            this.mapExtTransactionDetailToTransactionDetail(t),
        });
      })
    );
  }

  // endregion

  // region cashcuts

  @Action(GetCashcutList)
  private getCashcuts(ctx: StateContext<AppStateModel>): any {
    return this.merchantService
      .getCashcuts(
        0,
        ctx.getState().config.lazyLoadingChunkSize,
        ctx.getState().requestFilter
      )
      .pipe(
        tap(cashcutResponse => {
          ctx.setState({
            ...ctx.getState(),
            cashcutList: {
              page: cashcutResponse.number,
              size: cashcutResponse.size,
              isLast: cashcutResponse.last,
              items: cashcutResponse.content.map(c =>
                this.mapExtCashcutListItemToCashcutListItem(
                  c,
                  ctx.getState().currentMerchant.pointOfSales
                )
              ),
            },
          });
        })
      );
  }

  @Action(GetMoreCashcutListItems)
  private getMoreCashcuts(ctx: StateContext<AppStateModel>): any {
    const state = ctx.getState();
    const {page, size, isLast} = ctx.getState().cashcutList
      ? state.cashcutList
      : {page: -1, size: state.config.lazyLoadingChunkSize, isLast: false};

    if (isLast) {
      return of();
    } else {
      return this.merchantService
        .getCashcuts(page + 1, size, ctx.getState().requestFilter)
        .pipe(
          tap(cashcutResponse => {
            ctx.setState({
              ...state,
              cashcutList: {
                page: cashcutResponse.number,
                size: cashcutResponse.size,
                isLast: cashcutResponse.last,
                items: [
                  ...(state.cashcutList && state.cashcutList.items
                    ? state.cashcutList.items
                    : []),
                  ...cashcutResponse.content.map(c =>
                    this.mapExtCashcutListItemToCashcutListItem(
                      c,
                      ctx.getState().currentMerchant.pointOfSales
                    )
                  ),
                ],
              },
            });
          })
        );
    }
  }

  @Action(GetCashcutDetails)
  private getCashcutDetails(
    ctx: StateContext<AppStateModel>,
    action: GetCashcutDetails
  ): any {
    if (action.cashcutId == null) {
      // clear cashcut details
      ctx.setState({
        ...ctx.getState(),
        cashcutDetails: null,
      });
      return of();
    }

    if (
      ctx.getState().cashcutDetails &&
      ctx.getState().cashcutDetails.id === action.cashcutId
    ) {
      // do nothing, as we already have the right cashcut inside the state
      return of();
    }

    return this.merchantService.getCashcutDetail(action.cashcutId).pipe(
      tap(c => {
        ctx.setState({
          ...ctx.getState(),
          cashcutDetails: this.mapExtCashcutDetailsToCashcutDetails(
            c,
            ctx.getState().currentMerchant.pointOfSales
          ),
        });
      })
    );
  }

  // endregion

  // region reports

  @Action(SetCustomerSalesReportFilter)
  private setCustomerSalesReportFilter(
    ctx: StateContext<AppStateModel>,
    action: SetCustomerSalesReportFilter
  ): any {
    ctx.setState({
      ...ctx.getState(),
      customerSalesReportFilter: {
        ...action.filter
      },
    });

    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => ctx.dispatch(new GetCustomerSalesReport())),
      mergeMap(() => ctx.dispatch(new StopLoading()))
    );
  }

  @Action(GetCustomerSalesReport)
  private getCustomerSalesReport(ctx: StateContext<AppStateModel>): any {
    const emptyReport: ReportModel = {
      count: 0,
      data: '',
    };
    return this.merchantService
      .getCustomerSalesReport(ctx.getState().customerSalesReportFilter)
      .pipe(
        catchError(error => {
          console.log(error);
          return error.status !== 404
            ? this.translate.get('reports.errors.unknown-error').pipe(
              tap(i18n => this.toastr.error(i18n)),
              mergeMap(() => of(emptyReport))
            )
            : of(emptyReport);
        }),
        tap(customerSalesReport => {
          ctx.setState({
            ...ctx.getState(),
            customerSalesReport,
          });
        })
      );
  }

  @Action(DownloadCustomerSalesReport)
  private downloadCustomerSalesReport(ctx: StateContext<AppStateModel>): any {
    const {customerSalesReport, customerSalesReportFilter} = ctx.getState();

    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => {
        if (customerSalesReport != null && customerSalesReport.count > 0) {
          const dateFilterFrom = getDateString(
            customerSalesReportFilter.fromDate
          );
          const dateFilterTo = getDateString(customerSalesReportFilter.toDate);
          let baseName;
          switch (this.translate.currentLang) {
            case 'de':
              baseName = 'Umsatzreport';
              break;
            default:
              baseName = 'sales_report';
          }
          const filename = `${baseName}_${dateFilterFrom}-${dateFilterTo}.csv`;
          this.downloadFile(filename, customerSalesReport.data);
          return of(null);
        } else {
          return this.translate
            .get('reports.errors.no-data')
            .pipe(tap(i18n => this.toastr.warning(i18n)));
        }
      }),
      mergeMap(() => ctx.dispatch(new StopLoading()))
    );
  }

  // endregion

  // endregion

  // region routing

  @Action(RouteHome)
  private routeHome(ctx: StateContext<AppStateModel>): any {
    return ctx.dispatch(new Navigate(['']));
  }

  @Action(RouteLogin)
  private routeLogin(
    ctx: StateContext<AppStateModel>,
    action: RouteLogin
  ): any {
    return ctx.dispatch(
      new Navigate(action.merchantId ? ['login', action.merchantId] : ['login'])
    );
  }

  @Action(RouteRegister)
  private routeRegister(
    ctx: StateContext<AppStateModel>,
    action: RouteRegister
  ): any {
    return ctx.dispatch(new Navigate(['register', action.merchantId]));
  }

  @Action(RouteEmailMigration)
  private routeEmailMigration(
    ctx: StateContext<AppStateModel>,
    action: RouteEmailMigration
  ): any {
    return ctx.dispatch(new Navigate(['migrate-email', action.merchantId]));
  }

  @Action(RouteAccount)
  private routeAccount(ctx: StateContext<AppStateModel>): any {
    return ctx.dispatch(new Navigate(['account']));
  }

  @Action(RouteImprint)
  private routeImprint(
    ctx: StateContext<AppStateModel>,
    action: RouteImprint
  ): any {
    return ctx.dispatch(new Navigate(['imprint'], {fragment: action.anchor}));
  }

  @Action(RouteTransactionDetails)
  private routeTransactionDetails(
    ctx: StateContext<AppStateModel>,
    action: RouteTransactionDetails
  ): any {
    return ctx.dispatch(new Navigate(['transaction', action.saleId]));
  }

  @Action(RouteCashcutDetails)
  private routeCashcutDetails(
    ctx: StateContext<AppStateModel>,
    action: RouteCashcutDetails
  ): any {
    return ctx.dispatch(new Navigate(['cashcut', action.cashcutId]));
  }

  // endregion

  // region helper

  private saveCredentials(cardId: string, pin: string): void {
    window.localStorage.setItem(
      this.CREDENTIALS_KEY,
      JSON.stringify({cardId, pin})
    );
  }

  private deleteCredentials(): void {
    window.localStorage.removeItem(this.CREDENTIALS_KEY);
  }

  private getCredentials(): { cardId: string; pin: string } {
    const credentials = window.localStorage.getItem(this.CREDENTIALS_KEY);
    if (credentials) {
      try {
        return JSON.parse(credentials);
      } catch {
      }
    }
    return null;
  }

  private mapExtPosToPos(p: MerchantPosResponseModel): PosModel {
    return {
      id: p.id,
      name: p.name,
    };
  }

  private mapExtTransactionListItemToTransactionListItem(
    t: TransactionListItemResponseModel
  ): TransactionListItemModel {
    return {
      amount: t.amount,
      amountPayment: t.amountPayment,
      currency: t.currency,
      currencyPayment: t.currencyPayment,
      saleId: t.saleID,
      date: new Date(t.timestamp),
      type: t.type,
      pos: t.pos,
      reference: t.reference,
      transactionId: t.transactionId,
    } as TransactionListItemModel;
  }

  private mapExtTransactionDetailToTransactionDetail(
    t: TransactionDetailResponseModel
  ): TransactionDetailModel {
    return {
      editedAt: t.editedAt,
      printoutPreview: t.printoutPreview,
      amount: t.amount,
      currency: t.currency,
      amountPayment: t.amountPayment,
      currencyPayment: t.currencyPayment,
      saleId: t.saleID,
      date: new Date(t.timestamp),
      type: t.type,
      pos: t.pos,
      reference: t.reference,
      transactionId: t.transactionId,
    } as TransactionDetailModel;
  }

  private mapExtCashcutListItemToCashcutListItem(
    c: CashcutListItemResponseModel,
    pos: PosModel[]
  ): CashcutListItemModel {
    return {
      deviceId: c.deviceId,
      editedAt: c.editedAt,
      id: c.id,
      kassenschnittNr: c.kassenschnittNr,
      posId: c.posId,
      posName: pos
        ? pos.find(p => p.id === c.posId)
          ? pos.find(p => p.id === c.posId).name
          : `POS ${c.posId}`
        : `POS ${c.posId}`,
    };
  }

  private mapExtCashcutDetailsToCashcutDetails(
    c: CashcutDetailResponseModel,
    pos: PosModel[]
  ): CashcutDetailModel {
    return {
      deviceId: c.deviceId,
      editedAt: c.editedAt,
      id: c.id,
      kassenschnittNr: c.kassenschnittNr,
      posId: c.posId,
      posName: pos
        ? pos.find(p => p.id === c.posId)
          ? pos.find(p => p.id === c.posId).name
          : `POS ${c.posId}`
        : `POS ${c.posId}`,
      printoutPreview: c.printoutPreview,
    };
  }

  private downloadFile(filename, content): void {
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' + encodeURIComponent(content)
    );
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }

  // endregion
}
